import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import apiClient from '../api/api';
import toast from 'react-hot-toast';
import { useAuthStore } from './useAuthStore';

/**
 * useFinanceStore: Consolidación de lógica financiera crítica
 * Absorbe: walletStore.js + cartStore.js + subscriptionStore.js
 * 
 * Responsabilidad: Gestionar toda la lógica financiera de la aplicación
 * - Saldos de cartera (wallet)
 * - Historial de transacciones
 * - Carrito de compras
 * - Suscripciones y planes
 * 
 * ⚠️ CRÍTICO: Este store maneja datos sensibles. Requiere máxima integridad.
 */

const initialState = {
  // --- WALLET STATE ---
  walletBalance: 0,
  transactions: [],
  searchResults: [], // compat: walletStore.searchResults
  isLoading: false, // compat: walletStore.isLoading
  isSearching: false, // compat: walletStore.isSearching
  walletError: null,

  // --- CART STATE ---
  items: [], // compat: cartStore.items
  cartLoading: false,
  cartError: null,

  // --- SUBSCRIPTION STATE ---
  subscription: null,
  tier: 'free',
  benefits: ['acceso_noticias', 'comentarios_basicos', 'perfil_estandar'],
  tierName: 'Ciudadano',
  citizenship: {
    key: 'barrio',
    name: 'Barrio',
    level: 1,
    permissions: ['ver_publico'],
  },
  loading: false, // compat: subscriptionStore.loading
  error: null, // compat: subscriptionStore.error

  // --- PAYMENT METHODS STATE ---
  paymentMethods: [],
  defaultPaymentMethod: null,
};

export const useFinanceStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ============================================
        // --- WALLET ACTIONS (from walletStore) ---
        // ============================================

        /**
         * FetchTransactions: Obtiene el historial de transacciones del usuario
         */
        fetchTransactions: async () => {
          set({ isLoading: true, walletError: null });
          try {
            const response = await apiClient.get('/users/wallet/history');
            set({ transactions: response.data, isLoading: false });
            return response.data;
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'No se pudo cargar el historial de transacciones';
            toast.error(errorMsg);
            set({ isLoading: false, walletError: errorMsg });
            return [];
          }
        },

        /**
         * FetchWalletBalance: Obtiene el saldo actual de la cartera
         */
        fetchWalletBalance: async () => {
          set({ isLoading: true, walletError: null });
          try {
            const response = await apiClient.get('/wallet/balance');
            set({ walletBalance: response.data.balance, isLoading: false });
            return response.data.balance;
          } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al cargar el saldo';
            set({ isLoading: false, walletError: errorMsg });
            return 0;
          }
        },

        /**
         * SendCoins: Envía fondos a otro usuario
         */
        sendCoins: async (data) => {
          set({ isLoading: true, walletError: null });
          try {
            const normalizedAmount = Number(data?.amount);
            if (!Number.isInteger(normalizedAmount) || normalizedAmount <= 0) {
              const errorMsg = 'Ingresa un monto entero valido.';
              toast.error(errorMsg);
              set({ isLoading: false, walletError: errorMsg });
              return { success: false, message: errorMsg };
            }
            const payload = {
              ...data,
              amountMinor: Number.isInteger(data?.amountMinor)
                ? data.amountMinor
                : normalizedAmount * 100,
            };
            const response = await apiClient.post('/users/wallet/send', payload);
            toast.success(response.data.message);

            const nextBalance = response.data.senderCoins ?? response.data.newBalance ?? null;

            // Actualizar saldo en useAuthStore
            const authState = useAuthStore.getState();
            const updatedUser = {
              ...authState.currentUser,
              walletBalance: nextBalance ?? authState.currentUser?.walletBalance,
            };
            authState.setCurrentUser(updatedUser);

            // Actualizar estado local
            set((state) => ({
              walletBalance: nextBalance ?? state.walletBalance,
              isLoading: false,
            }));

            // Recargar transacciones
            await get().fetchTransactions();

            return { success: true, newBalance: nextBalance };
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'No se pudo enviar los fondos';
            toast.error(errorMsg);
            set({ isLoading: false, walletError: errorMsg });
            return { success: false };
          }
        },

        /**
         * RequestWithdrawal: Solicita un retiro de fondos
         */
        requestWithdrawal: async (data) => {
          set({ isLoading: true, walletError: null });
          try {
            const normalizedAmount = Number(data?.amount);
            if (!Number.isInteger(normalizedAmount) || normalizedAmount <= 0) {
              const errorMsg = 'Ingresa un monto entero valido.';
              toast.error(errorMsg);
              set({ isLoading: false, walletError: errorMsg });
              return { success: false, message: errorMsg };
            }
            const response = await apiClient.post('/withdrawals/request', data);
            toast.success(response.data.message);

            const nextBalance = response.data.walletBalance ?? response.data.coins ?? response.data.newBalance ?? null;

            // Actualizar saldo
            const authState = useAuthStore.getState();
            const updatedUser = {
              ...authState.currentUser,
              walletBalance: nextBalance ?? authState.currentUser?.walletBalance,
            };
            authState.setCurrentUser(updatedUser);

            set((state) => ({
              walletBalance: nextBalance ?? state.walletBalance,
              isLoading: false,
            }));

            await get().fetchTransactions();
            return { success: true, newBalance: nextBalance };
          } catch (error) {
            const errorCode = error.response?.data?.code || null;
            const nextAllowedAt = error.response?.data?.nextAllowedAt || null;
            const errorMsg =
              error.response?.data?.message || 'No se pudo procesar la solicitud';
            if (errorCode !== 'KYC_REQUIRED') {
              toast.error(errorMsg);
            }
            set({ isLoading: false, walletError: errorMsg });
            return { success: false, errorCode, message: errorMsg, nextAllowedAt };
          }
        },

        /**
         * SearchTransactions: Busca en el historial de transacciones
         */
        searchTransactions: async (query) => {
          set({ isSearching: true });
          try {
            const response = await apiClient.get('/wallet/transactions/search', {
              params: { q: query },
            });
            set({ searchResults: response.data, isSearching: false });
            return response.data;
          } catch (error) {
            console.error('Error searching transactions:', error);
            set({ isSearching: false, searchResults: [] });
            return [];
          }
        },

        // Compat: searchUsers / clearSearch / handleSuccessfulPurchase
        searchUsers: async (searchTerm) => {
          if (!searchTerm.trim()) {
            set({ searchResults: [] });
            return;
          }
          set({ isSearching: true });
          try {
            const response = await apiClient.get(`/users/search?q=${searchTerm}`);
            set({ searchResults: response.data });
          } catch (error) {
            // Silencioso para no interrumpir UX
          } finally {
            set({ isSearching: false });
          }
        },

        clearSearch: () => {
          set({ searchResults: [] });
        },

        handleSuccessfulPurchase: (amount) => {
          toast.success(`¡Se han añadido ${amount} fondos a tu cartera!`);
          const currentUser = useAuthStore.getState().currentUser;
          const newBalance = (currentUser?.walletBalance || 0) + amount;
          const updatedUser = { ...currentUser, walletBalance: newBalance };
          useAuthStore.getState().setCurrentUser(updatedUser);
          set({ walletBalance: newBalance });
          get().fetchTransactions();
        },

        // ============================================
        // --- CART ACTIONS (from cartStore) ---
        // ============================================

        /**
         * FetchCart: Obtiene los items del carrito
         */
        fetchCart: async () => {
          set({ cartLoading: true, cartError: null });
          try {
            const response = await apiClient.get('/cart');
            set({ items: response.data, cartLoading: false });
            return response.data;
          } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al cargar el carrito';
            console.error('Error al cargar el carrito:', error);
            set({ cartLoading: false, cartError: errorMsg });
            return [];
          }
        },

        /**
         * AddToCart: Añade un producto al carrito
         */
        addToCart: async (productId, quantity = 1) => {
          try {
            const response = await apiClient.post('/cart/add', {
              productId,
              quantity,
            });
            set({ items: response.data });
            toast.success('¡Producto añadido al carrito!');
            return { success: true, items: response.data };
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'No se pudo añadir el producto';
            toast.error(errorMsg);
            set({ cartError: errorMsg });
            return { success: false };
          }
        },

        /**
         * RemoveFromCart: Elimina un producto del carrito
         */
        removeFromCart: async (productId) => {
          const originalItems = get().items;
          // Actualización optimista
          set((state) => ({
            items: state.items.filter((item) => item.product._id !== productId),
          }));

          try {
            const response = await apiClient.delete(`/cart/remove/${productId}`);
            set({ items: response.data });
            toast.success('Producto eliminado del carrito.');
            return { success: true };
          } catch (error) {
            // Revertir cambios en caso de error
            set({ items: originalItems });
            const errorMsg =
              error.response?.data?.message || 'No se pudo eliminar el producto';
            toast.error(errorMsg);
            set({ cartError: errorMsg });
            return { success: false };
          }
        },

        /**
         * UpdateCartQuantity: Actualiza la cantidad de un producto en el carrito
         */
        updateCartQuantity: async (productId, newQuantity) => {
          const originalItems = get().items;

          // Actualización optimista
          set((state) => ({
            items: state.items.map((item) =>
              item.product._id === productId
                ? { ...item, quantity: newQuantity }
                : item
            ),
          }));

          try {
            const response = await apiClient.patch(`/cart/update/${productId}`, {
              quantity: newQuantity,
            });
            set({ items: response.data });
            return { success: true };
          } catch (error) {
            set({ items: originalItems });
            const errorMsg = error.response?.data?.message || 'Error al actualizar carrito';
            toast.error(errorMsg);
            return { success: false };
          }
        },

        /**
         * Checkout: Procesa el pago del carrito
         */
        checkout: async (paymentData) => {
          set({ cartLoading: true, cartError: null });
          try {
            const response = await apiClient.post('/cart/checkout', paymentData);
            toast.success('¡Compra realizada exitosamente!');

            // Limpiar carrito y actualizar wallet
            set({ items: [], cartLoading: false });

            // Actualizar saldo en useAuthStore
            const authState = useAuthStore.getState();
            if (response.data.newBalance !== undefined) {
              const updatedUser = {
                ...authState.currentUser,
                walletBalance: response.data.newBalance,
              };
              authState.setCurrentUser(updatedUser);
              set((state) => ({
                walletBalance: response.data.newBalance,
              }));
            }

            return { success: true, orderId: response.data.orderId };
          } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al procesar el pago';
            toast.error(errorMsg);
            set({ cartLoading: false, cartError: errorMsg });
            return { success: false };
          }
        },

        /**
         * GetCartTotal: Calcula el total del carrito
         */
        getCartTotal: () => {
          return get().items.reduce((total, item) => {
            return total + (item.product.price * item.quantity);
          }, 0);
        },

        /**
         * GetCartItemCount: Cuenta items en el carrito
         */
        getCartItemCount: () => {
          return get().items.reduce((count, item) => count + item.quantity, 0);
        },

        // Compat: cartStore.itemCount
        itemCount: () => {
          return get().items.reduce((count, item) => count + item.quantity, 0);
        },

        // ============================================
        // --- SUBSCRIPTION ACTIONS (from subscriptionStore) ---
        // ============================================

        /**
         * FetchSubscription: Obtiene la suscripción actual del usuario
         */
        fetchSubscription: async () => {
          set({ loading: true, error: null });
          try {
            const response = await apiClient.get('/tiers/me');
            const { subscription, citizenship } = response.data;

            set({
              subscription: subscription,
              tier: subscription.tier,
              tierName: subscription.tierName || 'Ciudadano',
              benefits: subscription.benefits || [],
              citizenship: citizenship || {
                key: 'barrio',
                name: 'Barrio',
                level: 1,
                permissions: ['ver_publico'],
              },
              loading: false,
            });

            return subscription;
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'Error al obtener suscripción';
            set({
              error: errorMsg,
              loading: false,
            });
            console.error('Error fetching subscription:', error);
            return null;
          }
        },

        /**
         * SelectPlan: Cambia el plan de suscripción del usuario
         */
        selectPlan: async (plan) => {
          set({ loading: true, error: null });
          try {
            const response = await apiClient.post('/tiers/select-plan', { plan });
            const { subscription, citizenship } = response.data;

            set({
              subscription: subscription,
              tier: subscription.tier,
              tierName: subscription.tierName || 'Ciudadano',
              benefits: subscription.benefits || [],
              citizenship: citizenship || {
                key: 'barrio',
                name: 'Barrio',
                level: 1,
                permissions: ['ver_publico'],
              },
              loading: false,
            });

            toast.success(`¡Plan actualizado a ${subscription.tierName}!`);
            return { success: true, subscription };
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'Error al cambiar plan';
            set({
              error: errorMsg,
              loading: false,
            });
            toast.error(errorMsg);
            return { success: false };
          }
        },

        /**
         * CancelSubscription: Cancela la suscripción actual
         */
        cancelSubscription: async () => {
          set({ loading: true, error: null });
          try {
            const response = await apiClient.post('/tiers/cancel-subscription');
            
            set({
              subscription: null,
              tier: 'free',
              tierName: 'Ciudadano',
              benefits: ['acceso_noticias', 'comentarios_basicos', 'perfil_estandar'],
              loading: false,
            });

            toast.success('Suscripción cancelada correctamente.');
            return { success: true };
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'Error al cancelar suscripción';
            set({
              error: errorMsg,
              loading: false,
            });
            toast.error(errorMsg);
            return { success: false };
          }
        },

        /**
         * UpgradePlan: Mejora el plan actual a uno superior
         */
        upgradePlan: async (newPlan) => {
          set({ loading: true, error: null });
          try {
            const response = await apiClient.post('/tiers/upgrade', {
              newPlan,
            });

            set({
              subscription: response.data.subscription,
              tier: response.data.subscription.tier,
              tierName: response.data.subscription.tierName,
              benefits: response.data.subscription.benefits || [],
              loading: false,
            });

            toast.success(`¡Plan actualizado a ${response.data.subscription.tierName}!`);
            return { success: true };
          } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al mejorar plan';
            set({
              error: errorMsg,
              loading: false,
            });
            toast.error(errorMsg);
            return { success: false };
          }
        },

        /**
         * HasBenefit: Verifica si el usuario actual tiene un beneficio
         */
        hasBenefit: async (benefit) => {
          try {
            const response = await apiClient.get(`/tiers/check-benefit/${benefit}`);
            return response.data.hasBenefit;
          } catch (error) {
            console.error('Error checking benefit:', error);
            return false;
          }
        },

        updateFromSocket: (data) => {
          set({
            tier: data.tier,
            tierName: data.tierName,
            benefits: data.benefits,
            citizenship:
              data.citizenship || {
                key: 'barrio',
                name: 'Barrio',
                level: 1,
                permissions: ['ver_publico'],
              },
          });
        },

        /**
         * ResetFinanceState: Limpia el estado (usado en logout)
         */
        resetFinanceState: () => {
          set(initialState);
        },
      }),
      {
        name: 'finance-storage', // Clave en localStorage
        partialize: (state) => ({
          items: state.items,
          tier: state.tier,
          tierName: state.tierName,
          benefits: state.benefits,
          citizenship: state.citizenship,
        }),
      }
    )
  )
);
